import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import TaxPayer from "../models/tax-payer";
import { Link } from "react-router-dom";
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
} from "@coreui/react";
import { Spinner } from "react-bootstrap";
import { getItem } from "../api/generics";
import {
  DocumentDashboard,
  DASHBOARD_TIME_ZONE,
  dashboardMetrics,
  documentsPath,
  statusLabels,
} from "../models/document-dashboard";
import { SUCCESS } from "../utils/constants/tags";

const formatCount = (value: number) => value.toLocaleString("es-PY");
const paraguayDate = (value: string) =>
  new Date(value).toLocaleDateString("es-PY", {
    timeZone: DASHBOARD_TIME_ZONE,
  });

const TaxPayerDashboard = ({ taxPayer }: { taxPayer: TaxPayer }) => {
  const [stats, setStats] = useState<DocumentDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getItem<DocumentDashboard>(
      "/stats/dashboard/",
      new Map([["tax_payer_id", String(taxPayer.id)]]),
    )
      .then((response) => {
        if (!active) return;
        if (response.status !== SUCCESS || !response.data)
          throw new Error("dashboard");
        setStats(response.data);
      })
      .catch(() => {
        if (active) {
          setError(true);
          setStats(null);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refresh, taxPayer.id]);

  const counts = stats?.totals;
  const errors = stats?.topErrors ?? [];

  return (
    <div aria-busy={loading}>
      <CCard>
        <CCardBody className="d-flex flex-wrap align-items-center justify-content-between">
          <div className="mr-3 mb-2">
            <h1 className="mb-1">Resumen de documentos</h1>
            <p className="text-muted mb-0">
              {taxPayer.socialName} · Documentos emitidos este mes pendientes
              de procesamiento y con errores
            </p>
          </div>
          <CButton
            color="primary"
            disabled={loading}
            onClick={() => setRefresh((value) => value + 1)}
          >
            {loading ? "Actualizando…" : "Actualizar"}
          </CButton>
        </CCardBody>
      </CCard>
      {error && (
        <CAlert color="danger">
          No se pudo cargar el resumen. Intente actualizar nuevamente.
        </CAlert>
      )}
      {loading && !stats && (
        <div className="text-center py-5" role="status">
          <Spinner animation="border" />
          <span className="sr-only">Cargando resumen</span>
        </div>
      )}
      {stats && counts && (
        <>
          <div className="d-flex flex-wrap justify-content-between align-items-end mb-3">
            <span className="text-muted small mt-2">
              Mes:{" "}
              {paraguayDate(stats.monthStart)} –{" "}
              {paraguayDate(stats.generatedAt)} · Hora de Paraguay
            </span>
          </div>
          <CRow>
            {dashboardMetrics.map(({ key, label, color }) => (
              <CCol md="6" key={key}>
                <CCard style={{ borderTop: `4px solid ${color}` }}>
                  <CCardBody>
                    <div className="text-muted mb-2">{label}</div>
                    <div style={{ color, fontSize: "2rem", fontWeight: 700 }}>
                      {formatCount(counts[key])}
                    </div>
                    <div className="small text-muted mt-1">
                      Todos los tipos
                    </div>
                  </CCardBody>
                </CCard>
              </CCol>
            ))}
          </CRow>
          <CCard>
            <CCardHeader>
              <strong>Por tipo de documento</strong>
              <span className="text-muted ml-2">
                Seleccione una cantidad para ver los documentos
              </span>
            </CCardHeader>
            <CCardBody>
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th scope="col">Documento</th>
                      {dashboardMetrics.map(({ key, label }) => (
                        <th scope="col" className="text-right" key={key}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stats.byType.map((row) => (
                      <tr key={row.type}>
                        <th scope="row">{row.label}</th>
                        {dashboardMetrics.map(({ key, metric, label }) => (
                          <td key={key} className="text-right">
                            <Link
                              to={documentsPath(row.type, metric)}
                              aria-label={`${row.label}: ${formatCount(row[key])} ${label.toLowerCase()}`}
                            >
                              {formatCount(row[key])}
                            </Link>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="small text-muted mb-0">
                Emisión según la fecha del documento, sin borradores ni
                eliminados. Se cuentan los documentos emitidos este mes cuyo
                estado actual es pendiente, generado, subido a SIFEN o con una
                solicitud en curso (reenvío, anulación, disconformidad o
                nominación); para recibos, pendiente o con anulación
                solicitada.
              </p>
            </CCardBody>
          </CCard>
          <CCard>
            <CCardHeader>
              <strong>Principales errores del mes</strong> ·{" "}
              Todos los tipos
            </CCardHeader>
            <CCardBody>
              <p className="text-muted small">
                Hasta 10 errores, priorizando los no recuperables y luego la
                cantidad de documentos afectados. Solo incluye documentos cuyo
                estado actual es de error y cuya emisión corresponde a este mes.
              </p>
              {errors.length === 0 ? (
                <CAlert color="success">
                  No hay documentos emitidos este mes con errores actuales para esta selección.
                </CAlert>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th scope="col">Estado</th>
                        <th scope="col">Código / descripción</th>
                        <th scope="col" className="text-right">
                          Documentos afectados
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {errors.map((row) => (
                        <tr key={JSON.stringify([row.status, row.errorKey])}>
                          <td>
                            <span
                              className={`badge badge-${row.status === "recoverable_error" ? "warning" : "danger"}`}
                            >
                              {statusLabels[row.status] ?? row.status}
                            </span>
                          </td>
                          <td
                            style={{ maxWidth: 600, overflowWrap: "anywhere" }}
                          >
                            <strong>{row.code ?? "Sin código"}</strong>
                            <div>
                              {statusLabels[row.description] ?? row.description}
                            </div>
                          </td>
                          <td className="text-right">
                            {formatCount(row.count)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CCardBody>
          </CCard>
          <p className="text-muted small text-right">
            Actualizado:{" "}
            {new Date(stats.generatedAt).toLocaleString("es-PY", {
              timeZone: DASHBOARD_TIME_ZONE,
            })}{" "}
            (Paraguay)
          </p>
        </>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );
  return <TaxPayerDashboard key={taxPayer.id} taxPayer={taxPayer} />;
};

export default DashboardPage;
